import httpclient from 'libs/Axios';
import { extractGeneratedDescription, getResponseList } from 'features/DigitalMarketing/utils/apiResponse';

export type RecognitionAchievementTab = 'achievements' | 'recognitions';

export type RecognitionAchievementItem = {
    id: number;
    recognitionsAchievementsId?: number;
    index: number;
    title: string;
    category: string;
    year: string;
    issuedBy: string;
    issuedFor: string;
    issuedLevel: string;
    description: string;
    image: string;
    isPublished: boolean;
    raw: any;
};

export type RecognitionAchievementPayload = {
    IMAGE_URL: string;
    TITLE: string;
    ISSUED_BY: string;
    ISSUED_YEAR: string;
    ISSUED_FOR: string;
    ISSUED_LEVEL: string;
    DESCRIPTION: string;
    CATEGORY: 'ACHIEVEMENT' | 'RECOGNITION';
};

const normalizeCategory = (category?: string) => String(category || '').trim().toUpperCase();

const getRecognitionId = (item: any, index: number) => Number(
    item?.RECOGNITIONS_ACHIEVEMENTS_ID
    || item?.recognitions_achievements_id
    || item?.recognitionsAchievementsId
    || item?.RECOGNITION_ID
    || item?.recognition_id
    || item?.Recognition_ID
    || item?.ID
    || item?.id
    || index,
);

const mapRecognitionAchievementRow = (item: any, index: number): RecognitionAchievementItem => {
    const recognitionsAchievementsId = getRecognitionId(item, index);
    const category = normalizeCategory(item?.CATEGORY || item?.category);

    return {
        id: recognitionsAchievementsId || index,
        recognitionsAchievementsId: recognitionsAchievementsId || undefined,
        index,
        title: item?.TITLE || item?.title || item?.ACHIEVEMENTS_TITLE || item?.RECOGNITION_ACHIEVEMENT || '',
        category,
        year: String(item?.ISSUED_YEAR || item?.issued_year || item?.ACHIEVEMENTS_YEAR || item?.RECOGNITION_YEAR || ''),
        issuedBy: item?.ISSUED_BY || item?.issued_by || item?.ACHIEVEMENTS_ISSUED_BY || item?.RECOGNITION_ISSUED_BY || '',
        issuedFor: item?.ISSUED_FOR || item?.issued_for || '',
        issuedLevel: item?.ISSUED_LEVEL || item?.issued_level || '',
        description: item?.DESCRIPTION || item?.description || item?.ACHIEVEMENTS_DESCRIPTION || item?.RECOGNITION_DESCRIPTION || '',
        image: item?.IMAGE_URL || item?.image_url || item?.ACHIEVEMENTS_IMAGE || item?.RECOGNITION_IMAGE || '',
        isPublished: Boolean(item?.IS_WEB_PUBLISHED || item?.is_web_published),
        raw: item,
    };
};

export const recognitionsAchievementsApi = {
    async list(pageSize = 10) {
        const response = await httpclient().get(`/master/branch/RecognitionInfo/list?page_size=${pageSize}&page_index=1`);
        const responseData = response?.data?.data || {};
        const rows = responseData.Recognitions || responseData.RECOGNITIONS || responseData.recognitions || getResponseList(response);

        return rows.map(mapRecognitionAchievementRow);
    },

    async listPaginated(pageIndex: number, pageSize: number) {
        const response = await httpclient().get(`/master/branch/RecognitionInfo/list?page_index=${pageIndex}&page_size=${pageSize}`);
        const responseData = response?.data?.data || {};
        const rows = responseData.Recognitions || responseData.RECOGNITIONS || responseData.recognitions || getResponseList(response);

        return {
            items: rows.map(mapRecognitionAchievementRow),
            total: Number(responseData.Total || responseData.TOTAL || responseData.total || rows.length || 0),
        };
    },

    async delete(recognitionId: number) {
        return httpclient().delete(`/master/branch/RecognitionInfo/delete?recognition_id=${recognitionId}`);
    },

    async publish(recognitionId: number, shouldPublish: boolean) {
        return httpclient().post(`/master/branch/RecognitionInfo/publish/${recognitionId}?is_web_published=${shouldPublish}`);
    },

    async generateDescription(payload: {
        TITLE: string;
        ISSUED_BY: string;
        ISSUED_YEAR: string;
        ISSUED_FOR: string;
        ISSUED_LEVEL: string;
    }) {
        const response = await httpclient().post('/master/recognitionAchievementDescription', payload);
        return extractGeneratedDescription(response);
    },

    async upsert(payload: RecognitionAchievementPayload) {
        return httpclient().post('/master/branch/RecognitionInfo/upsert', payload);
    },

    async update(recognitionId: number | undefined, payload: RecognitionAchievementPayload) {
        return httpclient().post('/master/branch/editRecognitionInfo', payload, {
            headers: {
                'recognition-id': recognitionId,
            },
        });
    },
};
